<script setup lang="ts" generic="T">
import {useI18n} from "vue-i18n";
import {computed, nextTick, PropType, ref, watch} from "vue";
import {useFormField} from "/@src/composable/form/useFormField";
import {useFormValidateRules} from "/@src/composable/form/useFormValidateRules";
import {AddonConfig, FormFieldLayout, FormFieldMenu} from "/@src/components/custom/form/BaseFormField.vue";
import {StateChecker, Transform} from "/@src/components/custom/form/BasePanelForm.vue";
import {FormValidatorRule} from "/@src/components/custom/form/BaseForm.vue";

const props = defineProps({
  label: String,
  field: {
    type: String,
  },
  limit: {
    type: Number,
    default: -1,
  },
  row:{
    type: Number,
    default: 4,
  },
  index: Number,
  required: Boolean,
  loading: Boolean,
  ignoreFieldUndefined: Boolean,
  icon: String,
  placeholder: String,
  modelValue: {
    type: String,
    default: '',
  },
  disabled: Boolean,
  deepWatch: {
    type: Boolean,
    default: false,
  },
  only: {
    type: Boolean,
    default: false,
  },
  transform: {
      type: Object as PropType<Transform<T,string>>
  },
  validateRule:{
    type: Object as PropType<FormValidatorRule>,
  },
  // 是否多行
  multiline:{
    type: Boolean,
    default: false,
  },
  layout: {
    type: String as PropType<FormFieldLayout>,
  },
  type:{
    type: String,
    default: 'text',
  },
  defaultValue: {
    type: String,
  },
  menus:{
    type: Array as PropType<FormFieldMenu[]>,
  },
  addonConfig:{
    type: Object as PropType<AddonConfig>,
  },
  labelAddonConfig:{
    type: Object as PropType<AddonConfig>,
  },
  checker:{
    type: Object as PropType<StateChecker<any>>,
  },
  // todo 根据trim决定是否trim
  trim:{
    type: Boolean,
    default: true,
  }
})
const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

const { t } = useI18n()
const refContainer = ref<HTMLDivElement>()
const localModel = ref(props.modelValue)
const addonValue = ref(null)
const fieldRef = ref(props.field)

watch(()=>props.modelValue,(newVal)=>{
  localModel.value = newVal
})

watch(()=>props.field,(newVal)=>{
  fieldRef.value = newVal
})
const {config,updateForm,formData} = useFormField<string>(localModel,fieldRef,{
  transform:(()=>{
    if (props.addonConfig) {
      return props.transform || {
        get:(data,field:string,value:string)=>{
          return value
        },
        set:(data,field:string,value:string)=>{
          const valueInForm = value + addonValue.value
          updateForm?.(props.field??null,valueInForm,props.index)
          emit('update:modelValue', valueInForm)
        }
      }
    }
    return props.transform
  })(),
  index: props.index,
  only: props.only,
  deepWatch: props.deepWatch,
  defaultValue: props.defaultValue,
  ignoreFieldUndefined: props.ignoreFieldUndefined,
  addonModel: addonValue,
  checker:props.checker||{
    isDisabled: ()=>props.disabled,
    isVisible: ()=>true,
  },
  dataWatcher:(newVal:string) => {
    let val = newVal + (addonValue.value??'')
    if (props.transform?.get){
      val = props.transform.get(formData.value,props.field,newVal,props.index)
    }
    emit('update:modelValue', val)
  }
})
const refLimit = ref<HTMLDivElement|null>(null)
let localValidateRule:FormValidatorRule|undefined = undefined
if (props.validateRule){
  // eslint-disable-next-line vue/no-setup-props-destructure
  localValidateRule = props.validateRule
} else {
  if (props.required || props.limit > 0) {
    localValidateRule = {
      required: props.required,
      limit: props.limit,
      trigger: ['change','init'],
      message:''
    }
  }
}

watch(()=>props.addonConfig,(newVal)=>{
  if (newVal?.select?.defaultVal&&addonValue.value === null){
    addonValue.value = newVal.select.defaultVal
  }
},{immediate:true})

const {validate,message} = useFormValidateRules(localModel,props.field,localValidateRule)

const isLoading = computed(()=>{
  return props.loading||config?.value.loading
})

const hasInputError = computed(()=>{
  if (isLoading.value){
    return false
  }
  if (props.required && !localModel.value){
    return true
  }
  return false
})

const fieldIcon = computed(()=>{
  if (props.multiline){
    return undefined
  }
  if (props.icon){
    return props.icon
  }
  return undefined
})

const handleBlur = () => {
  if (localValidateRule?.trigger === 'blur'){
    validate()
  }
}
// 测试，适配disabled等状态
const textColor = computed(() => {
  if (props.disabled) {
    return '#a1a1aa'
  } else {
    return ''
  }
})
const textColorDark = computed(() => {
  if (props.disabled) {
    return '#52525b'
  } else {
    return ''
  }
})
</script>

<template>
  <base-form-field ref="refContainer" class="form-field" :field="field" :index="index" :label="label" :disabled="disabled" :menus="menus" :layout="layout" :required="required"
                   :model-value="localModel"
      :addon-config="labelAddonConfig">
      <V-Field>
        <V-Control class="flex items-center gap-1" :icon="fieldIcon" :has-error="hasInputError" :loading="isLoading">
          <textarea v-if="multiline"
                    v-model="localModel"
                    @blur="handleBlur"
                    :disabled="disabled"
                    :rows="row"
                    :type="type"
                    class="textarea"
                    :placeholder="placeholder||t('placeholder-input')"/>
          <input
              v-else
              v-model.trim="localModel"
              :disabled="disabled"
              @blur="handleBlur"
              :type="type"
              class="input h-[38px] pl-9 disabled:bg-[#F5F5F5] dark:disabled:text-gray-400 bg-[hsl(0, 0%, 96%)] dark:bg-[var(--dark-sidebar-light-2)] dark:disabled:bg-[var(--dark-sidebar-light-10)]"
              :style="{'padding-right': limit>0 ? `${(refLimit?.clientWidth??0) + 5}px` : ''}"
              :placeholder="placeholder||t('editor-title-empty-placeholder')"
          />
          <div
              ref="refLimit"
              v-if="limit>0 && !isLoading"
              class="absolute right-2 top-[20%]"
              :class="[
                        (localModel?.length??0) > limit && 'text-rose-600'
                      ]"
          >
            {{ localModel?.length??0 }}/{{ limit }}
          </div>
          <Multiselect
              class="!min-w-[10%] !max-w-[40%]"
              v-if="addonConfig?.select"
              v-model="addonValue"
              :can-clear="false"
              :placeholder="placeholder"
              :options="addonConfig.select.options"

          />
        </V-Control>
        <!-- 表单错误显示 -->
        <div v-if="message" class="h-1.5 w-full text-sm !text-[var(--danger)]">{{message}}</div>
      </V-Field>

  </base-form-field>
</template>
<style lang="scss">
.form-field{
  .form-icon{
    svg{
      color: v-bind(textColor) !important;
    }
  }
}
.is-dark{
  .form-field{
    .form-icon{
      svg{
        color: v-bind(textColorDark) !important;
      }
    }
  }
}
</style>
